
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { CardImage } from "./card/CardImage";
import { CardDescription } from "./card/CardDescription";
import { CardContactItem } from "./card/CardContactItem";
import { CardActions } from "./card/CardActions";
import { SaveButton } from "./card/SaveButton";
import { NursingHome } from "@/hooks/use-nursing-home";
import { VerifiedBadge } from "./VerifiedBadge";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { MapPin, Phone, Star } from "lucide-react";
import { InspectionBadge } from "./badges/InspectionBadge";
import { FairDealBadge } from "./badges/FairDealBadge";
import { PriceDisplay } from "./badges/PriceDisplay";
import { MatchScoreBadge } from "./badges/MatchScoreBadge";
import { useMatchScore } from "@/hooks/use-match-score";
import { useQuery } from "@tanstack/react-query";

interface NursingHomeCardProps {
  nursingHome: NursingHome & {
    id?: number; // Added from search results
    distance?: number; // Added for location sort
  };
  hideBorder?: boolean;
  size?: "sm" | "md" | "lg";
}

export function NursingHomeCard({
  nursingHome,
  hideBorder = false,
  size = "md",
}: NursingHomeCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isInLeads, setIsInLeads] = useState(false);
  const { getScore } = useMatchScore();
  
  // Fetch review stats for this nursing home
  const { data: reviewStats } = useQuery({
    queryKey: ["review-stats", nursingHome.nh_slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("rating")
        .eq("nursing_home_id", nursingHome.nh_slug)
        .eq("is_published", true);
      if (error || !data || data.length === 0) return null;
      const avg = data.reduce((sum, r) => sum + r.rating, 0) / data.length;
      return { count: data.length, avg: Math.round(avg * 10) / 10 };
    },
    staleTime: 5 * 60 * 1000, // cache 5 min
  });

  const matchScore = getScore({
    county_slug: nursingHome.county_slug,
    county: nursingHome.county,
    fair_deal_approved: nursingHome.fair_deal_approved,
    inspection_rating: nursingHome.inspection_rating,
    wheelchair_accessible: nursingHome.wheelchair_accessible,
    specializations: nursingHome.specializations,
    category: nursingHome.category,
    price_range: nursingHome.price_range,
  });

  console.log("Rendering NursingHomeCard with data:", { 
    name: nursingHome.center_name,
    slug: nursingHome.nh_slug,
    county: nursingHome.county_slug,
    address: nursingHome.standardized_address
  });

  // Check if nursing home is in user's leads
  React.useEffect(() => {
    const checkLeads = async () => {
      if (user) {
        const { data, error } = await supabase
          .from("nursing_home_leads")
          .select("id")
          .eq("user_id", user.id)
          .eq("nursing_home_id", nursingHome.nh_slug)
          .limit(1);

        if (!error && data) {
          setIsInLeads(data.length > 0);
        }
      }
    };

    checkLeads();
  }, [user, nursingHome.nh_slug]);

  const handleCardClick = () => {
    if (nursingHome.county_slug && nursingHome.nh_slug) {
      navigate(`/nursing-homes/${nursingHome.county_slug}/${nursingHome.nh_slug}`);
    }
  };

  const handleTrackClick = (type: string) => {
    // Analytics tracking for click events
    console.log(`Tracking ${type} for ${nursingHome.center_name}`);
    // Here you would implement your actual tracking logic
  };

  // Determine nursing home category
  const category = nursingHome.category || "Nursing Home";
  
  // Format the location line to include town/city and county
  const getLocationLine = () => {
    // Use either new or old property names based on availability
    const townOrCity = nursingHome.town || nursingHome.city_town || "";
    const countyName = nursingHome.county || nursingHome.county_name || "";
    
    if (townOrCity && countyName) {
      return `${townOrCity}, ${countyName}`;
    } else if (townOrCity) {
      return townOrCity;
    } else if (countyName) {
      return countyName;
    }
    return "";
  };

  return (
    <Card
      className={`relative overflow-hidden transition-shadow duration-300 hover:shadow-lg ${
        !hideBorder ? "" : "border-none shadow-none"
      } flex flex-col group`}
    >
      
      <div className="flex-1" onClick={handleCardClick} role="button" tabIndex={0}>
        {/* Card Image */}
        <CardImage
          nhSlug={nursingHome.nh_slug}
          centerName={nursingHome.center_name || ""}
          address={nursingHome.standardized_address}
        />

        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className={`font-semibold truncate ${size === "sm" ? "text-md" : "text-xl"}`} title={nursingHome.center_name || "Unnamed Nursing Home"}>
                {nursingHome.center_name || "Unnamed Nursing Home"}
              </h3>
              <VerifiedBadge nhSlug={nursingHome.nh_slug} />
              <div className="flex flex-wrap gap-1.5 mt-1">
                <InspectionBadge rating={nursingHome.inspection_rating} size="sm" />
                <FairDealBadge approved={nursingHome.fair_deal_approved} size="sm" />
                {matchScore && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <MatchScoreBadge match={matchScore} variant="compact" />
                  </div>
                )}
              </div>
              {reviewStats && (
                <div className="flex items-center gap-1 mt-1.5">
                  <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                  <span className="text-sm font-medium">{reviewStats.avg}</span>
                  <span className="text-xs text-muted-foreground">({reviewStats.count})</span>
                </div>
              )}
              <p className="text-muted-foreground text-sm mt-1">
                {getLocationLine()}
              </p>
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              <SaveButton
                nursingHome={{
                  nh_slug: nursingHome.nh_slug,
                  center_name: nursingHome.center_name || "",
                  county_slug: nursingHome.county_slug || "",
                  county_name: nursingHome.county || nursingHome.county_name
                }}
                isInLeads={isInLeads}
                onTrackClick={handleTrackClick}
                className="relative top-0 right-0 bg-transparent border-0 shadow-none hover:bg-gray-50"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <CardDescription
            description={nursingHome.short_description || nursingHome.description}
            maxResidents={nursingHome.max_residents}
            category={category}
          />

          <div className="mt-4 space-y-2">
            {nursingHome.standardized_address && (
              <CardContactItem
                icon={MapPin}
                content={nursingHome.standardized_address}
                type="address"
              />
            )}

            {nursingHome.standardized_phone_number && (
              <CardContactItem
                icon={Phone}
                content={nursingHome.standardized_phone_number}
                type="phone"
              />
            )}
          </div>

          {nursingHome.price_range && (
            <div className="mt-3 pt-3 border-t">
              <PriceDisplay priceRange={nursingHome.price_range} size="sm" />
            </div>
          )}


          <div className="mt-4" onClick={(e) => e.stopPropagation()}>
              <CardActions
                centerName={nursingHome.center_name || ""}
                nhSlug={nursingHome.nh_slug}
                countySlug={nursingHome.county_slug || ""}
                websiteUrl={nursingHome.official_website}
                onTrackClick={handleTrackClick}
                isInLeads={isInLeads}
                setIsInLeads={setIsInLeads}
                email={nursingHome.email}
              />
          </div>
        </CardContent>
      </div>
    </Card>
  );
}
